"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Icon } from "./Icon";
import { useTranslations } from "next-intl";
import { AnimatedPlatformCard } from "./AnimatedPlatformCard";

export interface WorkflowStep {
  num: string;
  title: string;
  desc: string;
}

const STEP_METADATA: Record<string, { icon: string; accent: string; tag: string }> = {
  "01": { icon: "patient", accent: "#053C61", tag: "Longitudinal Access" },
  "02": { icon: "doc", accent: "#1E7BD7", tag: "Specialty Templates" },
  "03": { icon: "mic", accent: "#CC2D4A", tag: "Hands-Free VoiceMD" },
  "04": { icon: "diagnosis", accent: "#16A34A", tag: "Unified Care Orders" },
  "05": { icon: "network", accent: "#7C3AED", tag: "Inter-Dept Routing" },
  "06": { icon: "heart", accent: "#D97706", tag: "Progress & Outcomes" },
};

const ArrowRight = ({ className }: { className: string }) => (
  <svg className={`absolute z-0 text-slate-400 overflow-visible ${className}`} fill="none" viewBox="0 0 24 24" width="24" height="24">
    <motion.path 
      d="M0 12 L21 12" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeDasharray="4 4" 
      initial={{ strokeDashoffset: 8 }}
      animate={{ strokeDashoffset: 0 }}
      transition={{ duration: 0.6, repeat: Infinity, ease: "linear" }}
    />
    <path d="M15 6 L21 12 L15 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeft = ({ className }: { className: string }) => (
  <svg className={`absolute z-0 text-slate-400 overflow-visible ${className}`} fill="none" viewBox="0 0 24 24" width="24" height="24">
    <motion.path 
      d="M24 12 L3 12" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeDasharray="4 4" 
      initial={{ strokeDashoffset: 8 }}
      animate={{ strokeDashoffset: 0 }}
      transition={{ duration: 0.6, repeat: Infinity, ease: "linear" }}
    />
    <path d="M9 6 L3 12 L9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowDown = ({ className }: { className: string }) => (
  <svg className={`absolute z-0 text-slate-400 overflow-visible ${className}`} fill="none" viewBox="0 0 24 24" width="24" height="24">
    <motion.path 
      d="M12 0 L12 21" 
      stroke="currentColor" 
      strokeWidth="2.5" 
      strokeDasharray="4 4" 
      initial={{ strokeDashoffset: 8 }}
      animate={{ strokeDashoffset: 0 }}
      transition={{ duration: 0.6, repeat: Infinity, ease: "linear" }}
    />
    <path d="M6 15 L12 21 L18 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function EmrWorkflowVisualizer({ steps = [], chain = [] }: { steps?: WorkflowStep[], chain?: string[] }) {
  const t = useTranslations("specializedEmr.data");
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Fallback to avoid breaking if props aren't passed
  const workflowSteps = steps.length > 0 ? steps : [];
  const workflowChain = chain.length > 0 ? chain : [];

  useEffect(() => {
    setVisible(true);
  }, []);

  return (
    <div className="w-full mt-10" ref={ref}>
      {/* ── Steps Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative">
        {workflowSteps.map((step, i) => {
          const meta = STEP_METADATA[step.num] || { icon: "doc", accent: "#053C61", tag: t("workflowStepFallback") };

          return (
            <div
              key={step.num}
              className={`relative flex flex-col w-full ${
                // Desktop (3 cols): Snake layout
                i === 0 ? "lg:col-start-1 lg:row-start-1" :
                i === 1 ? "lg:col-start-2 lg:row-start-1" :
                i === 2 ? "lg:col-start-3 lg:row-start-1" :
                i === 3 ? "lg:col-start-3 lg:row-start-2" :
                i === 4 ? "lg:col-start-2 lg:row-start-2" :
                i === 5 ? "lg:col-start-1 lg:row-start-2" : ""
              } ${
                // Tablet (2 cols): Snake layout
                i === 0 ? "md:col-start-1 md:row-start-1" :
                i === 1 ? "md:col-start-2 md:row-start-1" :
                i === 2 ? "md:col-start-2 md:row-start-2" :
                i === 3 ? "md:col-start-1 md:row-start-2" :
                i === 4 ? "md:col-start-1 md:row-start-3" :
                i === 5 ? "md:col-start-2 md:row-start-3" : ""
              }`}
            >
              {/* Desktop Lines (lg) */}
              {(i === 0 || i === 1) && (
                <ArrowRight className="hidden lg:block top-1/2 -right-6 -translate-y-1/2" />
              )}
              {i === 2 && (
                <ArrowDown className="hidden lg:block -bottom-6 left-1/2 -translate-x-1/2" />
              )}
              {(i === 3 || i === 4) && (
                <ArrowLeft className="hidden lg:block top-1/2 -left-6 -translate-y-1/2" />
              )}

              {/* Tablet Lines (md) */}
              {i === 0 && (
                <ArrowRight className="hidden md:block lg:hidden top-1/2 -right-6 -translate-y-1/2" />
              )}
              {(i === 1 || i === 3) && (
                <ArrowDown className="hidden md:block lg:hidden -bottom-6 left-1/2 -translate-x-1/2" />
              )}
              {i === 2 && (
                <ArrowLeft className="hidden md:block lg:hidden top-1/2 -left-6 -translate-y-1/2" />
              )}
              {i === 4 && (
                <ArrowRight className="hidden md:block lg:hidden top-1/2 -right-6 -translate-y-1/2" />
              )}

              {/* Mobile Lines (sm) */}
              {i < workflowSteps.length - 1 && (
                <ArrowDown className="block md:hidden -bottom-6 left-1/2 -translate-x-1/2" />
              )}

              <AnimatedPlatformCard
                className="group flex-1 w-full relative bg-white/95 backdrop-blur-md rounded-lg p-6 sm:p-7 border border-slate-200/80 shadow-md hover:shadow-2xl hover:border-slate-300 transition-all duration-300 flex flex-col justify-between overflow-hidden cursor-pointer min-h-[230px]"
              >
              {/* Top Accent Gradient Border Line */}
              <div
                className="absolute top-0 left-0 right-0 h-1.5 transition-all duration-300 opacity-80 group-hover:opacity-100"
                style={{
                  background: `linear-gradient(90deg, ${meta.accent} 0%, ${meta.accent}88 100%)`,
                }}
              />

              <div>
                {/* Step Header */}
                <div className="flex items-center justify-between mb-5 relative z-10 gap-2">
                  <div className="flex items-center gap-3">
                    {/* Number Badge */}
                    <div
                      className="w-10 h-10 rounded-md flex items-center justify-center font-black text-sm text-white shadow-md transition-transform duration-300 group-hover:scale-110"
                      style={{ background: meta.accent }}
                    >
                      {step.num}
                    </div>

                    {/* Icon Container */}
                    <div
                      className="w-10 h-10 rounded-md flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                      style={{ background: `${meta.accent}14`, color: meta.accent }}
                    >
                      <Icon name={meta.icon} width={20} height={20} />
                    </div>
                  </div>

                  {/* Specialty / Tag Badge */}
                  <span
                    className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full border shadow-2xs whitespace-nowrap"
                    style={{
                      color: meta.accent,
                      borderColor: `${meta.accent}33`,
                      backgroundColor: `${meta.accent}08`,
                    }}
                  >
                    {meta.tag}
                  </span>
                </div>

                {/* Step Content */}
                <div className="relative z-10">
                  <h3 className="text-base sm:text-lg font-extrabold text-slate-900 tracking-tight leading-snug group-hover:text-[var(--ink,#053C61)] transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed mt-2.5 font-normal">
                    {step.desc}
                  </p>
                </div>
              </div>

              {/* Clean Minimalist Step Footer */}
              <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400 font-medium relative z-10">
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.accent }} />
                  {t("workflowStagePrefix")} {step.num} {t("workflowStageSuffix")}
                </span>
              </div>
            </AnimatedPlatformCard>
            </div>
          );
        })}
      </div>
    </div>
  );
}
